/**
 * Fornecedor e prestador de serviços: os campos do cadastro, usados em
 * Contatos (novo e edição) e no cadastro de fornecedor de Estoque e Compras.
 *
 * O fornecedor é UM cadastro só (`fornecedores`): a aba de Contatos e a de
 * Compras mostram a mesma linha. O prestador tem o tipo de serviço, do catálogo
 * da clínica (0159), e os dois levam a categoria de conta padrão.
 */

import { useEffect, useId, useState } from 'react';

import { Button, Icon, IconButton } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, Select, Switch, Textarea } from '@ds/forms';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { CategoriaParaEscolha, Fornecedor, PrestadorDeServico, TipoDeServico } from '@/data/tipos';

export const PRAZOS_DE_ENTREGA = ['3 dias úteis', '5 dias úteis', '7 dias úteis', '15 dias úteis'];

/** O valor do campo de tipo que abre o texto livre. */
export const OUTRO_TIPO = '__outros__';

/* --------------------------------------------------------------- cadastros de apoio */

export interface ApoioDoCadastro {
    categorias: CategoriaParaEscolha[];
    tipos: TipoDeServico[];
    erro: string | null;
    /** Um tipo criado agora entra na lista sem reler tudo. */
    incluirTipo: (tipo: TipoDeServico) => void;
}

export function useApoioDoCadastro(ativo: boolean): ApoioDoCadastro {
    const [categorias, setCategorias] = useState<CategoriaParaEscolha[]>([]);
    const [tipos, setTipos] = useState<TipoDeServico[]>([]);
    const [erro, setErro] = useState<string | null>(null);

    useEffect(() => {
        if (!ativo) return;
        let vivo = true;
        setErro(null);
        Promise.all([dados().categoriasParaCadastro(), dados().tiposDeServico()])
            .then(([c, t]) => { if (vivo) { setCategorias(c); setTipos(t); } })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível ler as categorias e os tipos de serviço.'); });
        return () => { vivo = false; };
    }, [ativo]);

    return {
        categorias, tipos, erro,
        incluirTipo: (tipo) => setTipos((atuais) => [...atuais.filter((t) => t.id !== tipo.id), tipo]),
    };
}

function CampoDeCategoria({ value, onChange, categorias, atual }: {
    value: string;
    onChange: (v: string) => void;
    categorias: CategoriaParaEscolha[];
    /** O nome da categoria gravada, que pode estar inativa ou fora do alcance de quem edita. */
    atual?: string;
}) {
    const id = useId();
    const opcoes = categorias.map((c) => ({ value: c.id, label: c.nome }));
    if (value && !opcoes.some((o) => o.value === value)) opcoes.unshift({ value, label: atual ?? 'Categoria atual' });
    return (
        <FormField label="Categoria de conta" htmlFor={id} hint="A categoria padrão dos lançamentos no Financeiro" style={{ gridColumn: '1 / -1' }}>
            <Select id={id} value={value} onChange={onChange} placeholder="Sem categoria" options={opcoes} />
        </FormField>
    );
}

/* -------------------------------------------------------------------- fornecedor */

export interface FormDoFornecedor {
    nome: string;
    cnpj: string;
    contato: string;
    telefone: string;
    email: string;
    prazoEntrega: string;
    categoriaId: string;
    ativo: boolean;
}

export function formDoFornecedor(f?: Fornecedor): FormDoFornecedor {
    return {
        nome: f?.nome ?? '', cnpj: f?.cnpj ?? '', contato: f?.contato ?? '', telefone: f?.telefone ?? '',
        email: f?.email ?? '', prazoEntrega: f?.prazoEntrega ?? (PRAZOS_DE_ENTREGA[1] as string),
        categoriaId: f?.categoriaId ?? '', ativo: f?.ativo ?? true,
    };
}

export function CamposDoFornecedor({ form, onChange, categorias, categoriaAtual, comAtivo = true }: {
    form: FormDoFornecedor;
    onChange: (mudanca: Partial<FormDoFornecedor>) => void;
    categorias: CategoriaParaEscolha[];
    categoriaAtual?: string;
    comAtivo?: boolean;
}) {
    const idNome = useId();
    const idCnpj = useId();
    const idPrazo = useId();
    const idContato = useId();
    const idTelefone = useId();
    const idEmail = useId();
    return (
        <>
            <FormField label="Razão social" required htmlFor={idNome} style={{ gridColumn: '1 / -1' }}>
                <Input id={idNome} value={form.nome} onChange={(v) => onChange({ nome: v })} placeholder="Ex.: Distribuidora MedSul" />
            </FormField>
            <FormField label="CNPJ" htmlFor={idCnpj}>
                <Input id={idCnpj} value={form.cnpj} onChange={(v) => onChange({ cnpj: v })} placeholder="00.000.000/0000-00" />
            </FormField>
            <FormField label="Prazo de entrega" htmlFor={idPrazo}>
                <Select id={idPrazo} value={form.prazoEntrega} onChange={(v) => onChange({ prazoEntrega: v })} options={PRAZOS_DE_ENTREGA.map((p) => ({ value: p, label: p }))} />
            </FormField>
            <FormField label="Contato" htmlFor={idContato}>
                <Input id={idContato} value={form.contato} onChange={(v) => onChange({ contato: v })} />
            </FormField>
            <FormField label="Telefone" htmlFor={idTelefone}>
                <Input id={idTelefone} type="tel" value={form.telefone} onChange={(v) => onChange({ telefone: v })} />
            </FormField>
            <FormField label="E-mail" htmlFor={idEmail} style={{ gridColumn: '1 / -1' }}>
                <Input id={idEmail} type="email" value={form.email} onChange={(v) => onChange({ email: v })} />
            </FormField>
            <CampoDeCategoria value={form.categoriaId} onChange={(v) => onChange({ categoriaId: v })} categorias={categorias} atual={categoriaAtual} />
            {comAtivo && (
                <div style={{ gridColumn: '1 / -1' }}>
                    <Switch
                        checked={form.ativo} label="Fornecedor ativo"
                        description="Inativo não aparece no cadastro de produtos nem nas entradas"
                        onChange={(v) => onChange({ ativo: v })}
                    />
                </div>
            )}
        </>
    );
}

export function salvarFornecedorDoForm(form: FormDoFornecedor, id?: string): Promise<Fornecedor> {
    return dados().salvarFornecedor({
        ...(id ? { id } : {}),
        nome: form.nome, cnpj: form.cnpj, contato: form.contato, telefone: form.telefone, email: form.email,
        prazoEntrega: form.prazoEntrega, ativo: form.ativo, categoriaId: form.categoriaId || null,
    });
}

/* ---------------------------------------------------------------------- prestador */

export interface FormDoPrestador {
    nome: string;
    documento: string;
    telefone: string;
    email: string;
    /** Id do tipo, `OUTRO_TIPO` ou vazio. */
    tipoServicoId: string;
    /** O tipo digitado em "Outros". */
    outroTipo: string;
    categoriaId: string;
    observacoes: string;
    ativo: boolean;
}

export function formDoPrestador(p?: PrestadorDeServico): FormDoPrestador {
    return {
        nome: p?.nome ?? '', documento: p?.documento ?? '', telefone: p?.telefone ?? '', email: p?.email ?? '',
        tipoServicoId: p?.tipoServicoId ?? '', outroTipo: '', categoriaId: p?.categoriaId ?? '',
        observacoes: p?.observacoes ?? '', ativo: p?.ativo ?? true,
    };
}

/** O que falta para salvar, ou nulo. */
export function problemaDoPrestador(form: FormDoPrestador): string | null {
    if (!form.nome.trim()) return 'Dê um nome ao prestador de serviços.';
    if (!form.telefone.trim()) return 'O telefone é obrigatório.';
    if (form.tipoServicoId === OUTRO_TIPO && !form.outroTipo.trim()) return 'Digite o tipo de serviço.';
    return null;
}

export function salvarPrestadorDoForm(form: FormDoPrestador, id?: string): Promise<PrestadorDeServico> {
    const outro = form.tipoServicoId === OUTRO_TIPO;
    return dados().salvarPrestador({
        ...(id ? { id } : {}),
        nome: form.nome, documento: form.documento, telefone: form.telefone, email: form.email, observacoes: form.observacoes,
        tipoServicoId: outro ? undefined : (form.tipoServicoId || null),
        ...(outro ? { novoTipoServico: form.outroTipo } : {}),
        categoriaId: form.categoriaId || null,
        ativo: form.ativo,
    });
}

/**
 * O tipo de serviço: a lista da clínica, "Outros" (abre o texto) e, dentro do
 * campo, o + que cria um tipo novo na hora e já o escolhe.
 */
export function CampoDoTipoDeServico({ value, outro, onChange, tipos, atual, onTipoCriado }: {
    value: string;
    outro: string;
    onChange: (mudanca: { tipoServicoId?: string; outroTipo?: string }) => void;
    tipos: TipoDeServico[];
    atual?: string;
    onTipoCriado: (tipo: TipoDeServico) => void;
}) {
    const aviso = useAviso();
    const idTipo = useId();
    const idOutro = useId();
    const idNovo = useId();
    const [criando, setCriando] = useState(false);
    const [novo, setNovo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);

    const opcoes = tipos
        .filter((t) => t.ativo || t.id === value)
        .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
        .map((t) => ({ value: t.id, label: t.ativo ? t.nome : `${t.nome} (inativo)` }));
    if (value && value !== OUTRO_TIPO && !opcoes.some((o) => o.value === value)) opcoes.unshift({ value, label: atual ?? 'Tipo atual' });
    opcoes.push({ value: OUTRO_TIPO, label: 'Outros' });

    const criar = async () => {
        if (!novo.trim()) {
            setErro('Digite o nome do tipo de serviço.');
            return;
        }
        setSalvando(true);
        setErro(null);
        try {
            const tipo = await dados().salvarTipoDeServico({ nome: novo });
            onTipoCriado(tipo);
            onChange({ tipoServicoId: tipo.id, outroTipo: '' });
            aviso({ title: 'Tipo de serviço criado', description: tipo.nome });
            setNovo('');
            setCriando(false);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível criar o tipo.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <>
            <FormField label="Tipo de serviço" htmlFor={idTipo} style={{ gridColumn: '1 / -1' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Select
                        id={idTipo} value={value} placeholder="Escolha o tipo" options={opcoes}
                        onChange={(v) => onChange({ tipoServicoId: v, ...(v === OUTRO_TIPO ? {} : { outroTipo: '' }) })}
                    />
                    <IconButton
                        icon={<Icon name="plus" size={16} />} label="Adicionar tipo de serviço" variant="outline" active={criando}
                        onClick={() => { setCriando((c) => !c); setErro(null); }}
                    />
                </div>
            </FormField>
            {value === OUTRO_TIPO && (
                <FormField label="Qual serviço?" required htmlFor={idOutro} hint="Vira um tipo novo da clínica ao salvar" style={{ gridColumn: '1 / -1' }}>
                    <Input id={idOutro} value={outro} onChange={(v) => onChange({ outroTipo: v })} placeholder="Ex.: Jardinagem" />
                </FormField>
            )}
            {criando && (
                <FormField label="Novo tipo de serviço" htmlFor={idNovo} error={erro ?? undefined} style={{ gridColumn: '1 / -1' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <Input
                            id={idNovo} value={novo} placeholder="Ex.: Ar-condicionado" invalid={!!erro}
                            onChange={(v) => { setNovo(v); setErro(null); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void criar(); } }}
                        />
                        <Button variant="secondary" loading={salvando} onClick={() => void criar()}>Adicionar tipo</Button>
                    </div>
                </FormField>
            )}
        </>
    );
}

export function CamposDoPrestador({ form, onChange, apoio, tipoAtual, categoriaAtual, comAtivo = true }: {
    form: FormDoPrestador;
    onChange: (mudanca: Partial<FormDoPrestador>) => void;
    apoio: ApoioDoCadastro;
    tipoAtual?: string;
    categoriaAtual?: string;
    comAtivo?: boolean;
}) {
    const idNome = useId();
    const idDocumento = useId();
    const idTelefone = useId();
    const idEmail = useId();
    const idObs = useId();
    return (
        <>
            <FormField label="Nome" required htmlFor={idNome} style={{ gridColumn: '1 / -1' }}>
                <Input id={idNome} value={form.nome} onChange={(v) => onChange({ nome: v })} placeholder="Pessoa ou empresa" />
            </FormField>
            <CampoDoTipoDeServico
                value={form.tipoServicoId} outro={form.outroTipo} tipos={apoio.tipos} atual={tipoAtual}
                onChange={(m) => onChange(m)} onTipoCriado={apoio.incluirTipo}
            />
            <FormField label="Telefone" required htmlFor={idTelefone}>
                <Input id={idTelefone} type="tel" value={form.telefone} onChange={(v) => onChange({ telefone: v })} placeholder="(11) 99999-9999" />
            </FormField>
            <FormField label="CPF ou CNPJ" htmlFor={idDocumento}>
                <Input id={idDocumento} value={form.documento} onChange={(v) => onChange({ documento: v })} />
            </FormField>
            <FormField label="E-mail" htmlFor={idEmail} style={{ gridColumn: '1 / -1' }}>
                <Input id={idEmail} type="email" value={form.email} onChange={(v) => onChange({ email: v })} placeholder="nome@email.com" />
            </FormField>
            <CampoDeCategoria value={form.categoriaId} onChange={(v) => onChange({ categoriaId: v })} categorias={apoio.categorias} atual={categoriaAtual} />
            <FormField label="Observações" htmlFor={idObs} style={{ gridColumn: '1 / -1' }}>
                <Textarea id={idObs} rows={2} value={form.observacoes} onChange={(v) => onChange({ observacoes: v })} maxLength={2000} />
            </FormField>
            {comAtivo && (
                <div style={{ gridColumn: '1 / -1' }}>
                    <Switch checked={form.ativo} label="Prestador ativo" onChange={(v) => onChange({ ativo: v })} />
                </div>
            )}
        </>
    );
}

export interface PrestadorModalProps {
    alvo: PrestadorDeServico | null;
    onClose: () => void;
    onSalvo: () => void;
}

/** Ver e editar um prestador já cadastrado. */
export function PrestadorModal({ alvo, onClose, onSalvo }: PrestadorModalProps) {
    const aviso = useAviso();
    const apoio = useApoioDoCadastro(Boolean(alvo));
    const [form, setForm] = useState(() => formDoPrestador(alvo ?? undefined));
    const sujo = useAlteracaoPendente(form, alvo);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setForm(formDoPrestador(alvo ?? undefined));
        setErro(null);
    }, [alvo]);

    if (!alvo) return null;

    const salvar = async () => {
        const problema = problemaDoPrestador(form);
        if (problema) {
            setErro(problema);
            return;
        }
        setSalvando(true);
        try {
            await salvarPrestadorDoForm(form, alvo.id);
            aviso({ title: 'Prestador atualizado', description: form.nome.trim() });
            onSalvo();
            onClose();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível salvar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open onClose={onClose} width={560}
            title={alvo.nome} description={alvo.tipoServico ?? 'Prestador de serviços'}
            dirty={sujo} onSave={() => void salvar()}
            footer={(
                <>
                    <Button variant="secondary" onClick={onClose}>Cancelar</Button>
                    <Button onClick={() => void salvar()} loading={salvando}>Salvar</Button>
                </>
            )}
        >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {(erro || apoio.erro) && (
                    <div style={{ gridColumn: '1 / -1' }}>
                        <Alert tone="danger" dismissible={false}>{erro ?? apoio.erro}</Alert>
                    </div>
                )}
                <CamposDoPrestador
                    form={form} onChange={(m) => { setForm((f) => ({ ...f, ...m })); setErro(null); }}
                    apoio={apoio} tipoAtual={alvo.tipoServico} categoriaAtual={alvo.categoria}
                />
            </div>
        </Modal>
    );
}
